/**
 * axios.ts — 공용 HTTP 인스턴스 + 인터셉터 (토큰/로케일/재시도/에러).
 *
 * 【역할】
 * - 프런트 전 영역에서 사용하는 단일 axios 인스턴스 — 모든 호출은 http.get/post 경유.
 * - 인증/언어/재시도/리다이렉트 로직을 인터셉터에 일원화하여 도메인 코드 경량화.
 *
 * 【주요 기능】
 * - 요청 인터셉터: JWT Bearer 자동 주입, X-Locale / Accept-Language 헤더
 * - 401 응답: refresh token 으로 1회 재시도 → 실패 시 /login 리다이렉트
 * - 403 응답: 세션 유지 + 'fw:http-warn' 이벤트 발행 (토스트만)
 * - 503 Runway/Storage unavailable: 전용 경고 토스트 + 후속 5xx 재시도 흐름 진입
 * - 5xx/네트워크: 지수 백오프(1s→2s) 최대 2회 재시도
 * - 연속 실패 카운터 (임계 3): 미달 시 경고, 임계 도달 시 서비스 불능 에러 + 로그인 리다이렉트
 * - 1회 성공 시 카운터 즉시 리셋 (LB 라우팅 회복 감지)
 *
 * 【상수】
 * - baseURL: /api (Vite proxy → http://localhost:24086)
 * - timeout: 30_000ms
 * - MAX_RETRIES: 2, RETRY_BASE_DELAY: 1000ms
 * - CONSECUTIVE_FAIL_THRESHOLD: 3
 *
 * 【발행 이벤트】
 * - fw:http-warn — 403, 503 특수 메시지, 5xx 재시도 실패(임계 미달)
 * - fw:http-error — 5xx 연속 실패 임계 도달 (서비스 불능 판정)
 *
 * 【왜 이 패턴?】
 * - axios 인터셉터로 인증/재시도를 중앙화해 각 훅이 error boundary 를 반복 구현하지 않도록.
 * - 지수 백오프 + 임계 카운터로 일시적 네트워크 요동과 실제 서비스 불능을 구분.
 */
import axios, { AxiosError, AxiosRequestConfig, InternalAxiosRequestConfig } from 'axios'
import i18n from '@/i18n'

const TOKEN_KEY = 'fw.accessToken'
const REFRESH_KEY = 'fw.refreshToken'

const MAX_RETRIES = 2
const RETRY_BASE_DELAY = 1000
const CONSECUTIVE_FAIL_THRESHOLD = 3

let consecutiveFailCount = 0
let isRedirecting = false

/**
 * 재시도 전 대기 — 지수 백오프 (1s, 2s, 4s...).
 * @param attempt - 0-based 재시도 카운트.
 */
function delay(attempt: number): Promise<void> {
  const ms = RETRY_BASE_DELAY * Math.pow(2, attempt)
  return new Promise((resolve) => setTimeout(resolve, ms))
}

/**
 * 재시도 가능 에러 판정 — /auth/ 엔드포인트 제외, 네트워크 에러 또는 5xx.
 */
function isRetryableError(error: AxiosError): boolean {
  const url = error.config?.url ?? ''
  if (url.includes('/auth/')) return false
  if (!error.response) return true
  return error.response.status >= 500
}

/** fw:http-warn 이벤트 발행 — FrameworkProvider 가 antd message.warning 으로 표시. */
function emitWarn(detail: {
  type: string
  message: string
  status?: number
  counter?: { current: number; threshold: number }
}) {
  if (typeof window === 'undefined') return
  window.dispatchEvent(new CustomEvent('fw:http-warn', { detail }))
}

/** fw:http-error 이벤트 발행 — FrameworkProvider 가 antd message.error 로 표시. */
function emitError(detail: { type: string; message: string }) {
  if (typeof window === 'undefined') return
  window.dispatchEvent(new CustomEvent('fw:http-error', { detail }))
}

// 본 시스템 백엔드는 /api/v1 prefix (V9-D1 도입 — backend 24 라우터 모두 v1)
export const http = axios.create({
  baseURL: '/api/v1',
  timeout: 30_000,
  headers: { 'Content-Type': 'application/json' }
})

http.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const token = localStorage.getItem(TOKEN_KEY)
  if (token && config.headers) {
    config.headers.Authorization = `Bearer ${token}`
  }
  const locale = localStorage.getItem('fw.locale')
  if (locale && config.headers) {
    config.headers['X-Locale'] = locale
    config.headers['Accept-Language'] = locale
  }
  return config
})

let refreshing: Promise<string | null> | null = null

/**
 * Refresh Token 으로 새 Access Token 발급 시도.
 * 성공 시 localStorage 갱신 + token 반환, 실패 시 모두 삭제 후 null.
 */
async function tryRefresh(): Promise<string | null> {
  const refreshToken = localStorage.getItem(REFRESH_KEY)
  if (!refreshToken) return null
  try {
    // V12-W2 fix: backend 는 v1 prefix 필수. 기존 '/api/auth/refresh' 는 404 → 401 시 강제 /login 튕김 원인.
    const res = await axios.post('/api/v1/auth/refresh', { refreshToken })
    const newToken = res.data?.accessToken ?? res.data?.data?.accessToken
    if (newToken) {
      localStorage.setItem(TOKEN_KEY, newToken)
      const newRefresh = res.data?.refreshToken ?? res.data?.data?.refreshToken
      if (newRefresh) localStorage.setItem(REFRESH_KEY, newRefresh)
      return newToken
    }
  } catch {
    localStorage.removeItem(TOKEN_KEY)
    localStorage.removeItem(REFRESH_KEY)
  }
  return null
}

/**
 * 로그인 페이지로 강제 이동 — 토큰/탭 상태 clear + 중복 리다이렉트 방지 플래그.
 * 이미 /login 에 있으면 skip, 3초 후 플래그 해제 (다음 세션 요청 대비).
 */
function redirectToLogin() {
  if (isRedirecting) return
  isRedirecting = true
  if (typeof window === 'undefined') return
  try {
    sessionStorage.removeItem('fw.tab.tabs')
    sessionStorage.removeItem('fw.tab.active')
    localStorage.removeItem(TOKEN_KEY)
    localStorage.removeItem(REFRESH_KEY)
  } catch {
    /* noop */
  }
  if (window.location.pathname !== '/login') {
    window.location.href = '/login'
  }
  setTimeout(() => {
    isRedirecting = false
    consecutiveFailCount = 0
  }, 3000)
}

http.interceptors.response.use(
  (res) => {
    consecutiveFailCount = 0
    return res
  },
  async (error: AxiosError) => {
    const original = error.config as
      | (AxiosRequestConfig & { _retry?: boolean; _retryCount?: number })
      | undefined
    const status = error.response?.status

    // ── 401: Access Token 만료 → Refresh Token 으로 1회 재시도 ──
    if (status === 401 && original && !original._retry) {
      original._retry = true
      if (!refreshing) refreshing = tryRefresh()
      const newToken = await refreshing
      refreshing = null
      if (newToken) {
        original.headers = {
          ...(original.headers || {}),
          Authorization: `Bearer ${newToken}`
        }
        return http.request(original)
      }
      redirectToLogin()
      return Promise.reject(error)
    }
    if (status === 401) {
      redirectToLogin()
      return Promise.reject(error)
    }

    // ── 503 Runway unavailable: 서킷 OPEN 알림 토스트 ──
    if (status === 503) {
      const data = error.response?.data as { message?: string; detail?: string } | undefined
      const detail = data?.detail ?? data?.message ?? ''
      if (detail === 'MSG_RUNWAY_UNAVAILABLE' || detail === 'MSG_STORAGE_UNAVAILABLE') {
        emitWarn({
          type: 'service-unavailable',
          status: 503,
          message: i18n.t(detail, {
            defaultValue: '외부 서비스를 일시적으로 사용할 수 없습니다.'
          }) as string
        })
      }
      // 일반 5xx 재시도 로직으로 계속 흐름
    }

    // ── 403: 권한 없음 → 토스트만, 세션 유지 ──
    if (status === 403) {
      const url = original?.url ?? ''
      if (!url.includes('/auth/')) {
        const data = error.response?.data as { message?: string } | undefined
        emitWarn({
          type: 'forbidden',
          status: 403,
          message:
            data?.message ??
            (i18n.t('MSG_PERMISSION_DENIED', { defaultValue: '권한이 없습니다.' }) as string)
        })
      }
      return Promise.reject(error)
    }

    // ── 5xx / 네트워크: 지수 백오프 재시도 → 연속 실패 임계 시 로그인 ──
    if (isRetryableError(error) && original) {
      const retryCount = original._retryCount ?? 0
      if (retryCount < MAX_RETRIES) {
        original._retryCount = retryCount + 1
        await delay(retryCount)
        return http.request(original)
      }
      consecutiveFailCount += 1
      if (consecutiveFailCount >= CONSECUTIVE_FAIL_THRESHOLD) {
        emitError({
          type: 'service-unavailable',
          message: i18n.t('MSG_SERVICE_UNAVAILABLE', {
            defaultValue: '서버에 연결할 수 없습니다. 잠시 후 다시 시도해주세요.'
          }) as string
        })
        redirectToLogin()
      } else {
        emitWarn({
          type: 'connection-issue',
          status,
          message: i18n.t('MSG_SLOW_CONNECTION', {
            defaultValue: `서버 응답 지연 중입니다. (${consecutiveFailCount}/${CONSECUTIVE_FAIL_THRESHOLD})`,
            0: consecutiveFailCount,
            1: CONSECUTIVE_FAIL_THRESHOLD
          }) as string,
          counter: { current: consecutiveFailCount, threshold: CONSECUTIVE_FAIL_THRESHOLD }
        })
      }
    }
    return Promise.reject(error)
  }
)

export default http
